/**
 * AuthSplash — minimal full-screen splash shown while the initial silent
 * auth check is in flight.
 *
 * Renders only the L4E brand mark and a subtle spinner with NO app chrome
 * (no LeftNav, no topbar, no avatar, no project content). Per audit F1 +
 * DEV-725, this is the only thing a non-authenticated user may see before
 * SessionGate decides where to send them (LoginPage, TotpVerifyScreen,
 * or the app shell).
 *
 * Intentionally framework-light — no app hooks, no i18n, no theme provider
 * dependencies beyond the global CSS custom properties. The component
 * must be safe to render even if downstream providers haven't initialized.
 */

import React from 'react';

const containerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minBlockSize: '100vh',
  background:
    'linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 50%, var(--bg-primary) 100%)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const cardStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: 32,
};

const logoStyle: React.CSSProperties = {
  inlineSize: 64,
  blockSize: 64,
  background:
    'linear-gradient(135deg, var(--accent-primary) 0%, var(--color-success) 100%)',
  borderRadius: 16,
  margin: '0 auto 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 28,
  fontWeight: 800,
  color: '#fff',
};

const spinnerStyle: React.CSSProperties = {
  inlineSize: 24,
  blockSize: 24,
  border: '2.5px solid var(--bg-active)',
  borderTopColor: 'var(--accent-primary)',
  borderRadius: '50%',
  margin: '24px auto 0',
  animation: 'l4e-auth-splash-spin 0.9s linear infinite',
};

const KEYFRAMES = `
  @keyframes l4e-auth-splash-spin {
    to { transform: rotate(360deg); }
  }
`;

export default function AuthSplash() {
  return (
    <div style={containerStyle} role="status" aria-live="polite" aria-label="Signing in">
      <style>{KEYFRAMES}</style>
      <div style={cardStyle}>
        <div style={logoStyle} aria-hidden="true">L4E</div>
        <div style={spinnerStyle} aria-hidden="true" />
      </div>
    </div>
  );
}
